/* ════════════ THE SPARK ════════════
   The you page's atmosphere: a field of dim, separate points.
   Every so often one lights up, and a moment later the two or
   three nearest catch it too. Adoption, animated: nobody lights
   the whole field. Everybody lights their neighbors. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function spark(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#spark")!;
  const ctx = c.getContext("2d")!;

  const GREEN = "#7dff9e";
  const GREY = "#9aa0a6";

  type Dot = {
    x: number;
    y: number;
    lit: number; // frames of glow remaining (0 = cold)
    fuse: number; // frames until this dot catches (queued by a neighbor)
  };
  let dots: Dot[] = [];
  let cooldown = 0; // frames until the next spontaneous ignition

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(90, Math.floor((c.width * c.height) / 22000));
    dots = Array.from({ length: count }, () => ({
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      lit: 0,
      fuse: 0,
    }));
    cooldown = 30;
  };
  seed();
  addEventListener("resize", seed, { signal });

  const ignite = (d: Dot) => {
    d.lit = 150 + Math.random() * 90;
    // the contagion: the closest few cold neighbors catch after a beat
    const near = dots
      .filter((o) => o !== d && o.lit === 0 && o.fuse === 0)
      .map((o) => ({ o, dist: (o.x - d.x) ** 2 + (o.y - d.y) ** 2 }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 2 + ((Math.random() * 2) | 0));
    for (const { o, dist } of near) {
      if (dist < 220 ** 2) o.fuse = 25 + Math.random() * 50;
    }
  };

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const d of dots) {
      if (d.lit > 0) {
        const a = Math.min(1, d.lit / 60);
        // soft halo, then the bright point
        ctx.globalAlpha = a * 0.12;
        ctx.fillStyle = GREEN;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = a * 0.6;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.globalAlpha = 0.16;
        ctx.fillStyle = GREY;
        ctx.fillRect(d.x - 0.75, d.y - 0.75, 1.5, 1.5);
      }
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    // static frame with a few lit, so the idea still reads
    for (let i = 0; i < dots.length; i += 9) dots[i]!.lit = 90;
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    if (--cooldown <= 0) {
      const cold = dots.filter((d) => d.lit === 0 && d.fuse === 0);
      if (cold.length) ignite(cold[(Math.random() * cold.length) | 0]!);
      cooldown = 140 + Math.random() * 200;
    }
    for (const d of dots) {
      if (d.fuse > 0 && --d.fuse === 0) ignite(d);
      if (d.lit > 0) d.lit--;
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(spark);
